import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  SourcesTable,
  SourcesCategoryFilter,
  AddSourcesMenu,
  FirstSourceEmptyState,
  CreateArticleView,
  AddDocumentsDialog,
  AddLinksDialog,
  AddGoogleDialog,
  PreviousTicketsWizard,
  type AddSourceKind,
  type SourcesCategorySelection
} from '@/modules/sources';
import { CategorizeLink } from '@/components/shared/categorize-link';
import { SearchInput } from '@/components/ui/input';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoCategorizeSources, useSources } from '@/hooks/use-sources';
import { useSopCategoryTree } from '@/hooks/use-sops';
import { invalidateQueries } from '@/lib/query-client';

/** Knowledge the AI answers from. Protocols — and the documents uploaded for them — live on the Protocols page. */
export default function Sources() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [linksDialogOpen, setLinksDialogOpen] = useState(false);
  const [googleDialogOpen, setGoogleDialogOpen] = useState(false);
  const [ticketsWizardOpen, setTicketsWizardOpen] = useState(false);
  const [creatingArticle, setCreatingArticle] = useState(false);

  // Kept in the URL so Back from a source's detail page lands on the same category.
  const sidebarSelection = useMemo<SourcesCategorySelection>(() => {
    const sopCatId = searchParams.get('sopCatId') ?? undefined;
    const category = searchParams.get('category') ?? undefined;
    const isUncategorised = searchParams.get('uncategorised') === 'true' ? true : undefined;
    return { sopCatId, category, isUncategorised };
  }, [searchParams]);

  const setSidebarSelection = (selection: SourcesCategorySelection) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('sopCatId');
      next.delete('category');
      next.delete('uncategorised');
      if (selection.sopCatId) next.set('sopCatId', selection.sopCatId);
      if (selection.category) next.set('category', selection.category);
      if (selection.isUncategorised) next.set('uncategorised', 'true');
      return next;
    });
  };

  const [searchQuery, setSearchQuery] = useState('');

  const { can } = usePermissions();
  const canEdit = can('edit', 'sources');
  const autoCategorize = useAutoCategorizeSources();

  const { data: categoryTree } = useSopCategoryTree();
  const { data: sources, isLoading } = useSources();
  const isCompletelyEmpty = !isLoading && (sources?.length ?? 0) === 0;

  // A document uploaded "for a protocol" is ingested here first and then becomes a protocol
  // draft; nothing pushes that completion, so refresh both lists while any source is processing.
  const hasInProgressSource = (sources ?? []).some((s) => s.status === 'INPROGRESS');
  useEffect(() => {
    if (!hasInProgressSource) return;
    const interval = setInterval(() => {
      invalidateQueries(['sources']);
      invalidateQueries(['sops']);
    }, 3000);
    return () => clearInterval(interval);
  }, [hasInProgressSource]);

  const openAddSourceDialog = (kind: AddSourceKind) => {
    if (kind === 'documents') setDocumentsDialogOpen(true);
    else if (kind === 'links') setLinksDialogOpen(true);
    else if (kind === 'google') setGoogleDialogOpen(true);
    else if (kind === 'tickets') setTicketsWizardOpen(true);
    else if (kind === 'article') setCreatingArticle(true);
  };

  if (creatingArticle) {
    return (
      <div className='flex flex-col p-4'>
        <CreateArticleView onCancel={() => setCreatingArticle(false)} onSaved={() => setCreatingArticle(false)} />
      </div>
    );
  }

  return (
    <div className='flex gap-4 flex-col p-4'>
      <div className='flex justify-between items-center'>
        <h1 className='text-base font-semibold'>Sources</h1>
        <div className='flex items-center gap-2'>
          {!isCompletelyEmpty && (
            <SearchInput
              placeholder='Search sources...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-[280px]'
            />
          )}
          {canEdit && <AddSourcesMenu onSelect={openAddSourceDialog} />}
        </div>
      </div>

      {isCompletelyEmpty ? (
        <FirstSourceEmptyState onSelect={openAddSourceDialog} />
      ) : (
        <div className='flex flex-col gap-4'>
          <SourcesTable
            categrizationId={sidebarSelection.sopCatId}
            isUncategorised={sidebarSelection.isUncategorised}
            searchQuery={searchQuery}
            leading={
              <SourcesCategoryFilter
                categories={categoryTree ?? []}
                selected={sidebarSelection}
                onSelect={setSidebarSelection}
                showCounts={false}
              />
            }
          />
          {canEdit && sidebarSelection.isUncategorised ? (
            <CategorizeLink onClick={() => autoCategorize.mutate()} pending={autoCategorize.isPending} />
          ) : null}
        </div>
      )}

      <AddDocumentsDialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen} />
      <AddLinksDialog open={linksDialogOpen} onOpenChange={setLinksDialogOpen} />
      <AddGoogleDialog open={googleDialogOpen} onOpenChange={setGoogleDialogOpen} />
      <PreviousTicketsWizard
        open={ticketsWizardOpen}
        onOpenChange={setTicketsWizardOpen}
        // Drafting protocols from tickets happens on the Protocols page.
        onOpenTicketCategorisation={() => navigate('/training/protocols?view=drafts&categorize=1')}
      />
    </div>
  );
}
